/**
 * External dependencies
 */
import { CodeEditor } from '@nab/code-editor';
import type { CustomPhpScopeRule, Maybe, ScopeRule } from '@nab/types';

/**
 * Internal dependencies
 */
import './style.scss';
import { CustomUrlScope } from '../custom-url-scope';
import { getScopeExplanation } from '../explanation';
import { useExperimentAttribute } from '../../../hooks';

export const UrlsOrPhpScope = (): JSX.Element => {
	const rules = useExperimentAttribute( 'scope' ) || [];
	const status = useExperimentAttribute( 'status' ) ?? 'running';

	const [ firstRule ] = rules;
	if ( isCustomPhpScopeRule( firstRule ) ) {
		return <PhpCode rule={ firstRule } />;
	}

	if ( ! rules.length ) {
		return <p>{ getScopeExplanation( 'urls', [], status ) }</p>;
	}

	return <CustomUrlScope scopeSupport="urls" />;
};

// ============
// HELPER VIEWS
// ============

const PhpCode = ( {
	rule,
}: {
	readonly rule: CustomPhpScopeRule;
} ): JSX.Element => {
	const status = useExperimentAttribute( 'status' ) ?? 'running';
	return (
		<div className="nab-edit-experiment-php-scope">
			<p>{ getScopeExplanation( 'urls-or-php', [ rule ], status ) }</p>
			<CodeEditor
				language="php"
				className="nab-edit-experiment-php-scope__snippet"
				value={ [
					'function is_test_active(): bool {',
					...rule.attributes.value.snippet
						.split( '\n' )
						.map( ( line ) => line.replaceAll( '\t', '  ' ) )
						.map( ( line ) => `  ${ line }` ),
					'}',
				].join( '\n' ) }
				readOnly
			/>
		</div>
	);
};

// =======
// HELPERS
// =======

export const isCustomPhpScopeRule = (
	r: Maybe< ScopeRule >
): r is CustomPhpScopeRule => 'php-snippet' === r?.attributes.type;
